<template>
  <div
    class="ms-login"
    :class="{ 'is-dark': isDarkSkin }"
    :style="loginThemeStyle"
  >
    <!-- Dark theme toggle hidden for now
    <button
      type="button"
      class="ms-theme-toggle"
      :aria-label="isDarkSkin ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleSkin"
    >
      <feather-icon :icon="isDarkSkin ? 'SunIcon' : 'MoonIcon'" size="18" />
      <span>{{ isDarkSkin ? "Light" : "Dark" }}</span>
    </button>
    -->

    <div class="ms-login-shell">
      <!-- Visual / brand panel -->
      <aside class="ms-login-visual">
        <div class="ms-visual-content">
          <div class="ms-logo-stage">
            <div class="ms-logo-shadow" />
            <div class="ms-logo-tilt">
              <b-img
                fluid
                :src="loginCoverSrc"
                alt="mySkool"
                class="ms-cover-img"
              />
            </div>
          </div>

          <p class="ms-visual-caption">
            <span class="ms-visual-caption-kicker"
              >School operations, simplified</span
            >
            Admissions, fees, attendance and academics - all from one place.
          </p>
        </div>
      </aside>

      <div class="ms-login-divider" aria-hidden="true" />

      <!-- Form panel -->
      <main class="ms-login-panel">
        <div class="ms-login-content">
          <div class="ms-login-mobile-brand d-lg-none mb-3">
            <b-img
              :src="$store.state.appConfig.appLogoImage || loginCoverSrc"
              alt="logo"
              class="ms-mobile-logo"
            />
          </div>

          <h1 class="ms-login-title">
            {{ $t("Welcome to") }}
            <span
              v-if="welcomeBrandName"
              class="ms-login-title-brand"
              :class="welcomeBrandSizeClass"
              >{{ displayWelcomeBrandName }}</span
            >
          </h1>
          <p class="ms-login-subtitle">
            {{
              $t(
                "Please enter your username and password to sign-in your account"
              )
            }}
          </p>

          <validation-observer ref="loginValidation">
            <b-form class="ms-login-form" @submit.prevent>
              <b-form-group
                :label="$t('Username')"
                label-for="login-email"
                class="ms-field"
              >
                <validation-provider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <div
                    class="ms-input-wrap"
                    :class="{ 'has-error': errors.length }"
                  >
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="ms-input-icon"
                    />
                    <b-form-input
                      id="login-email"
                      v-model="ulogin.username"
                      :state="errors.length > 0 ? false : null"
                      name="login-email"
                      :placeholder="$t('Enter your username')"
                      autocomplete="username"
                    />
                  </div>
                  <small class="text-danger">{{ $t(errors[0]) }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group class="ms-field">
                <div class="d-flex justify-content-between align-items-center">
                  <label for="login-password" class="mb-50">{{
                    $t("Password")
                  }}</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <div
                    class="ms-input-wrap ms-input-wrap--password"
                    :class="{ 'has-error': errors.length }"
                  >
                    <feather-icon
                      icon="LockIcon"
                      size="16"
                      class="ms-input-icon"
                    />
                    <b-form-input
                      id="login-password"
                      v-model="ulogin.password"
                      :state="errors.length > 0 ? false : null"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="············"
                      autocomplete="current-password"
                    />
                    <button
                      type="button"
                      class="ms-pass-toggle"
                      tabindex="-1"
                      @click="togglePasswordVisibility"
                    >
                      <feather-icon :icon="passwordToggleIcon" size="16" />
                    </button>
                  </div>
                  <small class="text-danger">{{ $t(errors[0]) }}</small>
                </validation-provider>
                <b-link
                  hidden
                  :to="{ name: 'auth-forgot-password-v2' }"
                  class="float-right"
                >
                  <small>{{ $t("Forgot Password?") }}</small>
                </b-link>
              </b-form-group>

              <b-button
                type="submit"
                class="ms-submit-btn"
                block
                :disabled="loading"
                @click="mylogin()"
              >
                <span class="d-inline-flex align-items-center">
                  {{ loading ? $t("Signing in") : $t("Sign in") }}
                  <feather-icon
                    v-if="!loading"
                    icon="ArrowRightIcon"
                    size="16"
                    class="ml-50"
                  />
                </span>
              </b-button>
            </b-form>
          </validation-observer>

          <p class="ms-login-footnote">
            Secure access for authorized school staff only
          </p>
        </div>
      </main>
    </div>

    <!-- Glass loading overlay on Sign in (no logo) -->
    <transition name="ms-splash">
      <div
        v-if="loading"
        class="ms-login-splash"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="ms-login-splash-glass">
          <p class="ms-login-splash-text">{{ $t("Signing in") }}</p>
          <div class="ms-login-splash-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BLink,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BImg,
  BForm,
  BButton,
  VBTooltip,
} from "bootstrap-vue";
import useJwt from "@/auth/jwt/useJwt";
import { required, email } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import store from "@/store/index";
import {
  ROLE_COA_ACCESS_SETTING_KEY,
  commitRoleCoaAccessFromSettingsResponse,
} from "@/utils/roleCoaAccess";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapActions } from "vuex";
import {
  resolveLoginTheme,
  loginThemeToCssVars,
  persistSplashTheme,
  applyBrandDocumentChrome,
  MAK_LOGO_URL,
} from "@/views/pages/authentication/loginThemes";

export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    BLink,
    BFormGroup,
    BFormInput,
    // eslint-disable-next-line vue/no-unused-components
    BFormCheckbox,
    BImg,
    BForm,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      status: "",
      password: "admin",
      userEmail: "admin@demo.com",
      sideImg: require("@/assets/images/pages/Logo-01.png"),
      ulogin: {
        username: "",
        password: "",
      },
      // validation rules
      required,
      email,
      loading: false,
      rights: {},
      hpgsCover: require("@/assets/images/logo/hpgs-logo.png"),
      hpgsSmall: require("@/assets/images/logo/hpgs-small.png"),
      szablcCover: require("@/assets/images/logo/szablc-cover.png"),
      szablcSmall: require("@/assets/images/logo/szablc-small.png"),
    };
  },
  computed: {
    hostname() {
      return window.location.hostname;
    },
    welcomeBrandName() {
      const host = (this.hostname || "").toLowerCase();
      // Domain-based schools — keep fixed brand names
      if (host === "connect.hpgs.pk") return "HPGS Connect";
      if (host.includes("iuss")) return "IUSS";
      if (host.includes("makcollegiate")) return "MAK Collegiate";
      if (host.includes("crestview")) return "Crest View";
      if (host.includes("elixir")) return "Elixir";
      if (host.includes("profectus")) return "Profectus";
      if (host.includes("shsschool") || host.startsWith("shs.")) return "SHS";
      if (host.includes("stanmore")) return "Stanmore";
      if (host.includes("szablc.edu.pk")) return "";

      // Themed schools (HPGS / IUSS / MAK / …) — keep theme display name
      if (
        this.loginTheme &&
        this.loginTheme.id !== "myskool" &&
        this.loginTheme.displayName
      ) {
        return this.loginTheme.displayName;
      }

      // Default / other schools — campus name from currentBranch.name
      const branch = this.$store.state.currentBranch;
      const campusName =
        branch && branch.name && String(branch.name).trim() !== "-"
          ? String(branch.name).trim()
          : "";
      if (campusName) return campusName;

      try {
        const stored = localStorage.getItem("myskool-welcome-brand");
        if (stored && stored.trim() && stored.trim() !== "mySkool") {
          return stored.trim();
        }
      } catch (e) {
        /* ignore */
      }

      return "mySkool";
    },
    /** Soften ALL-CAPS campus names so long titles stay readable */
    displayWelcomeBrandName() {
      const name = String(this.welcomeBrandName || "").trim();
      if (!name) return "";
      const letters = name.replace(/[^a-zA-Z]/g, "");
      const isAllCaps = letters.length > 0 && letters === letters.toUpperCase();
      if (isAllCaps && name.length > 22) {
        return name.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
      }
      return name;
    },
    welcomeBrandSizeClass() {
      const len = String(this.displayWelcomeBrandName || "").length;
      if (len > 48) return "is-xlong";
      if (len > 28) return "is-long";
      return "";
    },
    loginTheme() {
      let storedBrand = "";
      try {
        storedBrand = localStorage.getItem("myskool-welcome-brand") || "";
      } catch (e) {
        /* ignore */
      }
      return resolveLoginTheme({
        hostname: this.hostname,
        report: this.$store.state.report,
        brandName: storedBrand,
      });
    },
    loginThemeStyle() {
      return loginThemeToCssVars(this.loginTheme, { dark: this.isDarkSkin });
    },
    loginCoverSrc() {
      const fromStore = this.$store.state.appConfig.coverImage;
      if (fromStore && fromStore !== "-" && fromStore !== "null") {
        return fromStore;
      }
      try {
        const stored = localStorage.getItem("myskool-login-cover");
        if (stored && stored !== "-" && stored !== "null") return stored;
      } catch (e) {
        /* ignore */
      }
      return fromStore;
    },
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    isDarkSkin() {
      return this.$store.state.appConfig.layout.skin === "dark";
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require("@/assets/images/pages/Logo-01.png");
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  methods: {
    ...mapActions({
      getString: "getString",
    }),
    brandNameFromReport(report) {
      const key = String(report || "")
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
      if (!key) return "";

      const known = {
        myskoolhpgs: "HPGS",
        myskooliuss: "IUSS",
        myskoolmak: "MAK Collegiate",
        myskoolmakcollegiate: "MAK Collegiate",
        myskoolmakjauhar: "MAK Collegiate",
        myskoolmakhdoom: "Makhdoom",
        myskoolelixir: "Elixir",
        myskoolyakubov: "Yakubov",
        myskoolprofectus: "Profectus",
        myskoolcrestviewacademy: "Crest View",
        myskoolcrestview: "Crest View",
        myskoolaba: "ABA",
        myskoolksa: "KSA",
        shsschool: "SHS",
        myskoolshs: "SHS",
        myskoolshsschool: "SHS",
        myskoolstanmore: "Stanmore",
        myskool: "mySkool",
      };
      if (known[key]) return known[key];

      if (key.includes("shsschool") || key.includes("myskoolshs")) return "SHS";
      if (key.includes("crestview")) return "Crest View";
      if (key.includes("stanmore")) return "Stanmore";

      if (key.startsWith("myskool") && key.length > 7) {
        const rest = key.slice(7);
        if (
          rest === "mak" ||
          rest === "makcollegiate" ||
          rest === "makjauhar"
        ) {
          return "MAK Collegiate";
        }
        return rest.charAt(0).toUpperCase() + rest.slice(1);
      }
      return "";
    },
    toggleSkin() {
      const next = this.isDarkSkin ? "light" : "dark";
      this.$store.commit("appConfig/UPDATE_SKIN", next);
    },
    async fetchWithTimeout(url, ms = 2500) {
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), ms) : null;
      try {
        const response = await fetch(
          url,
          controller ? { signal: controller.signal } : undefined
        );
        return await response.json();
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
    async getUserIPAddress() {
      try {
        // Try to get IP from ipify service
        const data = await this.fetchWithTimeout(
          "https://api.ipify.org?format=json"
        );
        return data.ip;
      } catch (error) {
        console.log(
          "Error getting IP from ipify, trying alternative service:",
          error
        );
        try {
          // Fallback to another service
          const data = await this.fetchWithTimeout("https://ipapi.co/json/");
          return data.ip;
        } catch (fallbackError) {
          console.log("Error getting IP from fallback service:", fallbackError);
          return "Unknown";
        }
      }
    },
    logUserLogin(userIP, userData) {
      const loginLog = {
        timestamp: new Date().toISOString(),
        username: userData.username,
        userID: userData.localID,
        role: userData.role,
        ipAddress: userIP,
        userAgent: navigator.userAgent,
        campusID: userData.campusID,
        db: userData.db,
        loginStatus: "success",
      };
      // console.log('IP Address:', userIP);

      // Store in localStorage for now (in production, send to server)
      const existingLogs = JSON.parse(
        localStorage.getItem("userLoginLogs") || "[]"
      );
      existingLogs.push(loginLog);

      // Keep only last 100 login logs to prevent localStorage bloat
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }

      localStorage.setItem("userLoginLogs", JSON.stringify(existingLogs));
    },
    async mylogin() {
      //return console.log("Heloo");
      if (this.ulogin.username !== "" && this.ulogin.password !== "") {
        this.loading = true;
        var axios = require("axios");
        var data = this.ulogin;
        var mydata = {
          username: this.ulogin.email,
          password: this.ulogin.password,
        };
        var config = {
          method: "post",
          url: "https://connect.myskool.app/api/Tokens/UserLogin",
          data: data,
        };
        axios(config)
          .then(async (response) => {
            // console.log(response.data);
            this.username = response.data.data.username;
            this.role = response.data.data.role;

            // Get user IP address and log the login
            const userIP = await this.getUserIPAddress();
            this.logUserLogin(userIP, response.data.data);

            // Hit SetLoginLog API
            axios({
              method: "get",
              url: `https://apiv26.myskool.app/api/Users/SetLoginLog?db=${
                response.data.data.db
              }&userID=${
                response.data.data.localID
              }&dt=${new Date().toISOString()}`,
              headers: {
                Authorization: "bearer " + response.data.token,
              },
            }).catch((e) => console.log(e));

            if (
              response.data.data.role !== "parent" &&
              response.data.data.role !== "student" &&
              response.data.data.role !== "teacher"
            ) {
              //   if (response.data.data.status == 'active') {
              // console.log("MtData", response.data);
              this.$store.state.userData.token = response.data.token;
              this.$store.state.userData.cId = response.data.data.campusID;
              this.$store.state.userData.roles = response.data.data.role;
              this.$store.state.userData.username = response.data.data.username;
              this.$store.state.userData.db = response.data.data.db;
              this.$store.state.userData.userID = response.data.data.localID;
              this.$store.state.userData.password = this.ulogin.password;
              this.$store.state.userData.encPassword =
                response.data.data.password;
              // this.$store.state.userData.userID = response.data.data.id;
              //console.log( this.$store.state.userData.token);
              // console.log("))))))))))", this.$store.state.userData);
              this.$store.commit(
                "setDomain",
                "https://apiv26.myskool.app/api/"
              );
              // this.$store.commit(
              //   "setDomain",
              //   "https://staging.myskool.app/api/"
              // );
              this.$store.commit(
                "verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED",
                true
              );

              var obj = {
                url:
                  this.$store.state.domain +
                  "Campuses/GetClientID?db=" +
                  this.$store.state.userData.db,
                token: this.$store.state.userData.token,
              };
              let res = await this.getString(obj);
              this.$store.commit("setReport", res);
              // console.log(this.$store.state.report);

              var config = {
                url:
                  this.$store.state.domain +
                  "Users/GetSelected?id=" +
                  this.$store.state.userData.userID +
                  "&db=" +
                  this.$store.state.userData.db,
                headers: {
                  Authorization: "bearer " + this.$store.state.userData.token,
                },
              };
              axios(config)
                .then((res) => {
                  this.$store.commit("setLoggedUser", res.data.data);

                  let mm = JSON.parse(res.data.data.forms);
                  let obj = mm.find((el) =>
                    el.children.find(
                      (ch) =>
                        !ch.view &&
                        !ch.add &&
                        !ch.edit &&
                        !ch.delete &&
                        ch.permission
                    )
                  );
                  // console.log(obj);
                  if (obj) {
                    let child = obj.children.find(
                      (ch) =>
                        !ch.view &&
                        !ch.add &&
                        !ch.edit &&
                        !ch.delete &&
                        ch.permission
                    );
                    // console.log(child)
                    this.$store.commit("setPermissions", child.permission);
                    obj.children = obj.children.filter(
                      (el) => el.view || el.add || el.edit || el.delete
                    );
                    if (obj.children.length == 0) {
                      mm = mm.filter((el) => el.id != obj.id);
                    } else {
                      let ind = mm.findIndex((el) => el.id == obj.id);
                      mm.splice(ind, 1, obj);
                    }
                  } else {
                    let other = mm.find((el) =>
                      el.children.find((ch) => ch.permission)
                    );
                    // console.log(other);
                    if (other) {
                      let otherCh = other.children.find((ch) => ch.permission);
                      this.$store.commit("setPermissions", otherCh.permission);
                      // console.log(otherCh);
                    } else this.$store.commit("setPermissions", "");
                  }
                  // console.log(mm);
                  this.$store.commit("setMenu", mm);

                  // this.$store.commit(
                  //   "setMenu",
                  //   JSON.parse(res.data.data.forms)
                  // );
                  // console.log(this.$store.state.menu);
                  this.$store.commit(
                    "setCurrentMenu",
                    this.$store.state.menu[0]
                  );
                  // console.log(this.$store.state.currentMenu);

                  this.$store.state.menu.forEach((el) => {
                    // console.log(el["icon"]);
                    el.children.forEach((ch) => {
                      this.rights[ch.route] = {
                        view: ch.view,
                        add: ch.add,
                        edit: ch.edit,
                        delete: ch.delete,
                      };
                    });
                  });
                  // console.log(this.rights);
                  this.$store.commit("setRights", this.rights);
                  // console.log(this.$store.state.rights);

                  axios({
                    method: "post",
                    url:
                      this.$store.state.domain +
                      "Settings/getSettingsAll?db=" +
                      this.$store.state.userData.db,
                    data: [ROLE_COA_ACCESS_SETTING_KEY],
                    headers: {
                      Authorization:
                        "bearer " + this.$store.state.userData.token,
                    },
                  })
                    .then((settingRes) => {
                      commitRoleCoaAccessFromSettingsResponse(
                        this.$store,
                        settingRes.data,
                        this.$store.state.userData.cId
                      );
                    })
                    .catch(() => {
                      commitRoleCoaAccessFromSettingsResponse(
                        this.$store,
                        [],
                        this.$store.state.userData.cId
                      );
                    });

                  // Keep glass splash until route completes
                  //this.$router.push({ name: 'dashboard-ecommerce'})

                  // this.$router.push({ path: "/" });
                  useJwt
                    .login({
                      email: "admin@demo.com",
                      password: "admin",
                    })
                    .then((response) => {
                      // console.log(response);
                      const { userData } = response.data;
                      // console.log("UserData", userData);
                      userData.fullName = this.$store.state.userData.username;
                      userData.role = this.$store.state.userData.roles;
                      useJwt.setToken(response.data.accessToken);
                      useJwt.setRefreshToken(response.data.refreshToken);
                      localStorage.setItem(
                        "userData",
                        JSON.stringify(userData)
                      );

                      this.$ability.update(userData.ability);

                      this.$store.commit(
                        "app-ecommerce/UPDATE_CART_ITEMS_COUNT",
                        0
                      );

                      // for page after login
                      const homeRoute =
                        (this.$store.state.currentMenu &&
                          this.$store.state.currentMenu.children &&
                          this.$store.state.currentMenu.children[0] &&
                          this.$store.state.currentMenu.children[0].route) ||
                        "dashboard";

                      this.$router
                        .replace({
                          name: getHomeRouteForLoggedInUser(homeRoute),
                        })
                        .then(() => {
                          this.$toast({
                            component: ToastificationContent,
                            position: "top-right",
                            props: {
                              title: `Welcome Administrator`,
                              icon: "CoffeeIcon",
                              variant: "success",
                              text: `You have successfully logged in as ${this.role}. Now you can start to explore!`,
                            },
                          });
                          this.$store.commit(
                            "appConfig/UPDATE_CONTENT_WIDTH",
                            "boxed"
                          );
                        })
                        .catch(() => {
                          this.loading = false;
                        });
                    })
                    .catch((error) => {
                      this.loading = false;
                      if (
                        this.$refs.loginForm &&
                        error &&
                        error.response &&
                        error.response.data
                      ) {
                        this.$refs.loginForm.setErrors(
                          error.response.data.error
                        );
                      }
                    });
                })
                .catch((error) => {
                  // console.log(error);
                  this.loading = false;
                });
            } else {
              this.$bvToast.toast(
                "Please check your email or password is correct.",
                {
                  title: this.$t("Error!"),
                  variant: "danger",
                  solid: true,
                }
              );
              return (this.loading = false);
            }
          })
          .catch((error) => {
            // console.log(error);
            this.$bvToast.toast(
              "Please check your email or password is correct.",
              {
                title: this.$t("Error!"),
                variant: "danger",
                solid: true,
              }
            );
            this.loading = false;
          });
      } else {
        this.$bvToast.toast(this.$t("Please fill the form correctly."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
      // this.$refs.loginForm.validate().then((success) => {
      //   if (success) {
      //   }
      // });
    },
  },
  watch: {
    "$store.state.appConfig.coverImage"(url) {
      if (!url || url === "-" || url === "null") return;
      try {
        localStorage.setItem("myskool-login-cover", url);
      } catch (e) {
        /* ignore */
      }
      applyBrandDocumentChrome(this.loginTheme, url);
    },
  },
  created() {
    if (
      window.location.hostname == "connect.hpgs.pk" ||
      window.location.hostname.includes("connect.hpgs")
    ) {
      this.$store.commit("appConfig/UPDATE_COVERIMAGE", this.hpgsCover);
      this.$store.commit("appConfig/UPDATE_APP_LOGOIMAGE", this.hpgsCover);
      try {
        localStorage.setItem("myskool-welcome-brand", "HPGS Connect");
        localStorage.setItem("myskool-splash-theme-id", "hpgs-connect");
        localStorage.setItem("myskool-splash-logo", "/hpgs-logo.png");
        localStorage.setItem("myskool-splash-logo-source", "app");
        localStorage.setItem("myskool-login-cover", "/hpgs-logo.png");
      } catch (e) {
        /* ignore */
      }
    } else if (
      window.location.hostname === "portal.makcollegiate.com" ||
      window.location.hostname.includes("makcollegiate")
    ) {
      this.$store.commit("appConfig/UPDATE_COVERIMAGE", MAK_LOGO_URL);
      this.$store.commit("appConfig/UPDATE_APP_LOGOIMAGE", MAK_LOGO_URL);
      try {
        localStorage.setItem("myskool-welcome-brand", "MAK Collegiate");
        localStorage.setItem("myskool-splash-theme-id", "mak");
        localStorage.setItem("myskool-splash-logo", MAK_LOGO_URL);
        localStorage.setItem("myskool-splash-logo-source", "app");
        localStorage.setItem("myskool-login-cover", MAK_LOGO_URL);
        localStorage.setItem("myskool-favicon", MAK_LOGO_URL);
      } catch (e) {
        /* ignore */
      }
    } else if (window.location.hostname.includes("szablc.edu.pk")) {
      this.$store.commit("appConfig/UPDATE_COVERIMAGE", this.szablcCover);
      this.$store.commit("appConfig/UPDATE_APP_LOGOIMAGE", this.szablcSmall);
    }
    // Sync welcome brand from persisted report (covers logout → login)
    if (!localStorage.getItem("myskool-welcome-brand")) {
      const fromReport = this.brandNameFromReport(this.$store.state.report);
      if (fromReport) {
        try {
          localStorage.setItem("myskool-welcome-brand", fromReport);
        } catch (e) {
          /* ignore */
        }
      }
    }
    // Keep login cover available for favicon + next visit
    const cover = this.$store.state.appConfig.coverImage;
    if (cover && cover !== "-" && cover !== "null") {
      try {
        localStorage.setItem("myskool-login-cover", cover);
      } catch (e) {
        /* ignore */
      }
    }
    // Cache splash loader colors for next cold load
    persistSplashTheme(this.loginTheme);
    applyBrandDocumentChrome(this.loginTheme, cover);
    // console.log(this.$store.state.userData.password);
    // this.$store.commit("setDomain", "https://staging.myskool.app/api/");
    // this.$store.commit("appConfig/UPDATE_LAYOUT_TYPE", "vertical");
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Sora:wght@600;700;800&display=swap");

.ms-login {
  --ms-bg: #f7f6fb;
  --ms-panel: #ffffff;
  --ms-ink: #1e1b2e;
  --ms-muted: #6b6680;
  --ms-line: rgba(108, 92, 231, 0.16);
  --ms-accent: #6c5ce7;
  --ms-accent-soft: #a29bfe;
  --ms-accent-deep: #5b4cc4;
  --ms-input: #ffffff;
  --ms-shadow: 0 16px 40px rgba(108, 92, 231, 0.1);
  --ms-accent-rgb: 108, 92, 231;
  --ms-accent-soft-rgb: 162, 155, 254;
  --ms-gradient: var(--ms-accent);
  --ms-gradient-hover: var(--ms-accent-deep);
  --ms-brand: var(--ms-accent);
  --ms-font: "Plus Jakarta Sans", "Segoe UI", sans-serif;
  --ms-display: "Sora", "Plus Jakarta Sans", sans-serif;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: var(--ms-bg);
  color: var(--ms-ink);
  position: relative;
  overflow: hidden;
  font-family: var(--ms-font);
}

.ms-theme-toggle {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--ms-line);
  background: rgba(255, 255, 255, 0.72);
  color: var(--ms-ink);
  backdrop-filter: blur(10px);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 24px rgba(var(--ms-accent-rgb), 0.12);

  .is-dark & {
    background: rgba(36, 31, 51, 0.85);
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(var(--ms-accent-rgb), 0.18);
  }
}

.ms-login-shell {
  min-height: 100vh;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0;
  padding: 2.5rem 2rem;
  box-sizing: border-box;
}

.ms-login-divider {
  width: 1px;
  height: min(420px, 58vh);
  align-self: center;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(var(--ms-accent-rgb), 0.45) 20%,
    rgba(var(--ms-accent-rgb), 0.45) 80%,
    transparent 100%
  );
  margin: 0 2.25rem;
}

.ms-login-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1.25rem 1.5rem 0.5rem;
  overflow: visible;
  min-height: auto;
  border-radius: 0;
  background: transparent;
}

.ms-visual-content {
  position: relative;
  z-index: 1;
  width: min(520px, 100%);
  text-align: center;
}

.ms-brand-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--ms-line);
  background: rgba(255, 255, 255, 0.7);
  color: var(--ms-muted);
  font-family: var(--ms-font);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  .is-dark & {
    background: rgba(42, 36, 59, 0.7);
  }
}

.ms-brand-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ms-accent);
}

.ms-logo-stage {
  position: relative;
  perspective: 1100px;
  width: min(480px, 100%);
  margin: 0 auto;
  padding: 0.5rem 0 1.5rem;
}

.ms-logo-shadow {
  position: absolute;
  left: 50%;
  bottom: 0.15rem;
  width: 58%;
  height: 22px;
  transform: translateX(-50%);
  background: radial-gradient(
    ellipse at center,
    rgba(var(--ms-accent-rgb), 0.2) 0%,
    transparent 70%
  );
  filter: blur(6px);
  animation: ms-logo-shadow 10s ease-in-out infinite;
  opacity: 0.65;
  transition: opacity 0.4s ease;
}

.ms-logo-tilt {
  position: relative;
  transform-style: preserve-3d;
  will-change: transform;
  animation: ms-logo-float 10s ease-in-out infinite;
  transition: filter 0.4s ease;
  cursor: pointer;
}

.ms-logo-stage:hover .ms-logo-tilt {
  animation: ms-logo-float-hover 7s ease-in-out infinite;
  filter: drop-shadow(0 22px 28px rgba(var(--ms-accent-rgb), 0.22));
}

.ms-logo-stage:hover .ms-logo-shadow {
  animation: ms-logo-shadow-hover 7s ease-in-out infinite;
}

.ms-cover-img {
  max-height: 390px;
  width: auto;
  max-width: 100%;
  margin: 0 auto;
  object-fit: contain;
  filter: drop-shadow(0 18px 30px rgba(var(--ms-accent-rgb), 0.16));
}

.ms-visual-caption {
  margin: 1.15rem auto 0;
  max-width: 380px;
  color: var(--ms-muted);
  font-family: var(--ms-font);
  font-size: 0.98rem;
  font-weight: 500;
  line-height: 1.65;
  letter-spacing: -0.01em;
  text-align: center;
}

.ms-visual-caption-kicker {
  display: block;
  margin-bottom: 0.4rem;
  color: var(--ms-accent);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.ms-login-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  isolation: isolate;
}

.ms-login-content {
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  animation: ms-rise 0.55s ease both;
}

.ms-mobile-logo {
  height: 48px;
  width: auto;
  object-fit: contain;
}

.ms-login-title {
  margin: 0 0 0.55rem;
  font-family: var(--ms-display);
  font-size: clamp(1.95rem, 2.9vw, 2.4rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.12;
  color: var(--ms-ink);
}

.ms-login-title-brand {
  display: block;
  margin-top: 0.2rem;
  color: var(--ms-accent);
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.ms-login-title-brand.is-long {
  font-size: clamp(1.2rem, 2.1vw, 1.5rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.28;
}

.ms-login-title-brand.is-xlong {
  font-size: clamp(1.02rem, 1.85vw, 1.28rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.32;
}

.ms-login-subtitle {
  margin: 0 0 1.85rem;
  color: var(--ms-muted);
  font-family: var(--ms-font);
  font-size: 0.94rem;
  font-weight: 500;
  line-height: 1.65;
  letter-spacing: -0.01em;
}

.ms-field {
  margin-bottom: 1.2rem;

  ::v-deep label,
  label {
    font-family: var(--ms-font);
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--ms-ink);
    letter-spacing: -0.01em;
  }
}

.ms-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--ms-input);
  border: 1px solid var(--ms-line);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(var(--ms-accent-rgb), 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:focus-within {
    border-color: var(--ms-accent);
    box-shadow: 0 0 0 4px rgba(var(--ms-accent-rgb), 0.14);
    transform: translateY(-1px);
  }

  &.has-error {
    border-color: #e74c3c;
  }

  ::v-deep .form-control {
    border: 0 !important;
    background: transparent !important;
    background-image: none !important;
    box-shadow: none !important;
    height: 50px;
    padding-left: 2.55rem;
    padding-right: 0.95rem;
    color: var(--ms-ink);
    font-family: var(--ms-font);
    font-size: 0.95rem;
    border-radius: 16px;

    &::placeholder {
      color: var(--ms-muted);
      opacity: 0.72;
    }

    /* Kill browser/extension autofill wallpaper (e.g. TempMail logos) */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: var(--ms-ink) !important;
      caret-color: var(--ms-ink);
      background-color: transparent !important;
      background-image: none !important;
      transition: background-color 99999s ease-in-out 0s;

      box-shadow: 0 0 0 1000px var(--ms-input) inset !important;
    }
  }

  &--password ::v-deep .form-control {
    padding-right: 2.7rem;
  }
}

.ms-input-icon {
  position: absolute;
  left: 1rem;
  color: var(--ms-accent);
  pointer-events: none;
  opacity: 0.9;
}

.ms-pass-toggle {
  position: absolute;
  right: 0.45rem;
  border: 0;
  background: transparent;
  color: var(--ms-muted);
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: var(--ms-accent);
    background: rgba(var(--ms-accent-rgb), 0.1);
  }
}

.ms-submit-btn {
  margin-top: 0.7rem;
  height: 50px;
  border: 0 !important;
  border-radius: 16px !important;
  font-family: var(--ms-font) !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  background: var(--ms-accent) !important;
  box-shadow: 0 10px 26px rgba(var(--ms-accent-rgb), 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: var(--ms-accent-deep) !important;
    box-shadow: 0 14px 30px rgba(var(--ms-accent-rgb), 0.38);
  }

  &:disabled {
    opacity: 0.75;
  }
}

.ms-login-footnote {
  margin: 1.35rem 0 0;
  text-align: center;
  color: var(--ms-muted);
  font-family: var(--ms-font);
  font-size: 0.74rem;
  font-weight: 500;
}

@keyframes ms-logo-float {
  0%,
  100% {
    transform: translateY(0) rotateX(3deg) rotateY(-6deg) translateZ(0);
  }
  25% {
    transform: translateY(-7px) rotateX(0deg) rotateY(4deg) translateZ(8px);
  }
  50% {
    transform: translateY(-3px) rotateX(-3deg) rotateY(7deg) translateZ(5px);
  }
  75% {
    transform: translateY(-8px) rotateX(2deg) rotateY(-3deg) translateZ(10px);
  }
}

@keyframes ms-logo-float-hover {
  0%,
  100% {
    transform: translateY(-2px) rotateX(-2deg) rotateY(8deg) translateZ(6px);
  }
  33% {
    transform: translateY(-11px) rotateX(5deg) rotateY(-7deg) translateZ(16px);
  }
  66% {
    transform: translateY(-5px) rotateX(-5deg) rotateY(5deg) translateZ(10px);
  }
}

@keyframes ms-logo-shadow {
  0%,
  100% {
    width: 55%;
    opacity: 0.5;
  }
  50% {
    width: 66%;
    opacity: 0.78;
  }
}

@keyframes ms-logo-shadow-hover {
  0%,
  100% {
    width: 58%;
    opacity: 0.55;
  }
  50% {
    width: 74%;
    opacity: 0.95;
  }
}

@keyframes ms-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .ms-login-shell {
    grid-template-columns: 1fr;
    max-width: 480px;
    padding: 5rem 1.25rem 2.5rem;
    gap: 0;
  }

  .ms-login-visual,
  .ms-login-divider {
    display: none;
  }

  .ms-login-panel {
    min-height: auto;
    padding: 0;
  }

  .ms-login-content {
    max-width: 100%;
  }

  .ms-theme-toggle {
    top: 1rem;
    right: 1rem;
  }
}

@media (min-width: 992px) and (max-width: 1199.98px) {
  .ms-login-shell {
    max-width: 960px;
    padding: 2rem 1.5rem;
  }

  .ms-login-divider {
    margin: 0 1.5rem;
  }

  .ms-cover-img {
    max-height: 320px;
  }
}

/* —— Sign-in glass splash (no logo) —— */
.ms-login-splash {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.18);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  backdrop-filter: blur(22px) saturate(170%);
}

.is-dark .ms-login-splash {
  background: rgba(15, 15, 25, 0.36);
}

.ms-login-splash-glass {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 50px;
  min-width: 220px;
  padding: 0.85rem 1.75rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(var(--ms-accent-rgb), 0.16);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.1);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  backdrop-filter: blur(14px) saturate(140%);
}

.is-dark .ms-login-splash-glass {
  background: rgba(30, 28, 40, 0.72);
  border-color: rgba(var(--ms-accent-rgb), 0.28);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
}

.ms-login-splash-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.ms-login-splash-dots span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--ms-accent);
  animation: msLoginDot 1.15s ease-in-out infinite;
}

.ms-login-splash-dots span:nth-child(2) {
  animation-delay: 0.18s;
  background: var(--ms-accent-soft);
}

.ms-login-splash-dots span:nth-child(3) {
  animation-delay: 0.36s;
}

.ms-login-splash-text {
  margin: 0;
  font-family: var(--ms-font);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
  color: var(--ms-ink);
  white-space: nowrap;
}

.ms-splash-enter-active,
.ms-splash-leave-active {
  transition: opacity 0.28s ease;
}

.ms-splash-enter-active .ms-login-splash-glass,
.ms-splash-leave-active .ms-login-splash-glass {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.ms-splash-enter,
.ms-splash-leave-to {
  opacity: 0;
}

.ms-splash-enter .ms-login-splash-glass,
.ms-splash-leave-to .ms-login-splash-glass {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

@keyframes msLoginDot {
  0%,
  80%,
  100% {
    transform: translateY(0) scale(0.85);
    opacity: 0.45;
  }
  40% {
    transform: translateY(-6px) scale(1.15);
    opacity: 1;
  }
}
</style>
